import React, { useMemo } from 'react';

export interface LineChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface OverviewLineChartProps {
  data: LineChartDataPoint[];
  title: string;
  color?: string;
  height?: number;
  showGrid?: boolean;
  showPoints?: boolean;
  className?: string;
}

export const OverviewLineChart: React.FC<OverviewLineChartProps> = ({
  data,
  title,
  color = 'blue',
  height = 300,
  showGrid = true,
  showPoints = true,
  className = '',
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { points: [], maxValue: 1, minValue: 0 };
    
    const values = data.map(d => d.value);
    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);
    
    const padding = 40;
    const chartWidth = 800;
    const chartHeight = height;
    const graphWidth = chartWidth - padding * 2;
    const graphHeight = chartHeight - padding * 2;
    
    const points = data.map((point, index) => {
      const x = padding + (index / (data.length - 1 || 1)) * graphWidth;
      const y = padding + graphHeight - ((point.value - minValue) / (maxValue - minValue || 1)) * graphHeight;
      return { x, y, ...point };
    });
    
    return { points, maxValue, minValue, chartWidth, chartHeight, padding, graphWidth, graphHeight };
  }, [data, height]);

  const getColorClasses = () => {
    const colors: Record<string, { stroke: string; fill: string; point: string }> = {
      blue: {
        stroke: 'stroke-blue-500',
        fill: 'fill-blue-500/20',
        point: 'fill-blue-500',
      },
      green: {
        stroke: 'stroke-green-500',
        fill: 'fill-green-500/20',
        point: 'fill-green-500',
      },
      red: {
        stroke: 'stroke-red-500',
        fill: 'fill-red-500/20',
        point: 'fill-red-500',
      },
      purple: {
        stroke: 'stroke-purple-500',
        fill: 'fill-purple-500/20',
        point: 'fill-purple-500',
      },
      orange: {
        stroke: 'stroke-orange-500',
        fill: 'fill-orange-500/20',
        point: 'fill-orange-500',
      },
    };
    return colors[color] || colors.blue;
  };

  const colorClasses = getColorClasses();

  const generatePath = (points: Array<{ x: number; y: number }>) => {
    if (points.length === 0) return '';
    return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  };

  const generateAreaPath = (points: Array<{ x: number; y: number }>) => {
    if (points.length === 0) return '';
    const { chartHeight, padding } = chartData;
    const linePath = generatePath(points);
    const lastPoint = points[points.length - 1];
    const firstPoint = points[0];
    return `${linePath} L ${lastPoint.x} ${chartHeight - padding} L ${firstPoint.x} ${chartHeight - padding} Z`;
  };

  if (!data || data.length === 0) {
    return (
      <div className={`card p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={`card p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${chartData.chartWidth} ${chartData.chartHeight}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid Lines */}
          {showGrid && (
            <g className="text-gray-300 dark:text-gray-700">
              {[0, 1, 2, 3, 4].map((i) => {
                const y = chartData.padding + (i / 4) * chartData.graphHeight;
                return (
                  <g key={i}>
                    <line
                      x1={chartData.padding}
                      y1={y}
                      x2={chartData.chartWidth - chartData.padding}
                      y2={y}
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                    <text
                      x={chartData.padding - 10}
                      y={y + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-500 dark:fill-gray-400"
                    >
                      {Math.round(chartData.maxValue - (i / 4) * (chartData.maxValue - chartData.minValue))}
                    </text>
                  </g>
                );
              })}
            </g>
          )}

          {/* Area Fill */}
          <path
            d={generateAreaPath(chartData.points)}
            className={colorClasses.fill}
          />

          {/* Line */}
          <path
            d={generatePath(chartData.points)}
            className={colorClasses.stroke}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data Points */}
          {showPoints &&
            chartData.points.map((point, index) => (
              <g key={index}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  className={colorClasses.point}
                />
                <title>
                  {point.label || point.date}: {point.value.toLocaleString()}
                </title>
              </g>
            ))}
        </svg>
      </div>
    </div>
  );
};
